import {
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventUpdatedEvent,
  MbscEventUpdateEvent,
} from '@mobiscroll/react';
import { differenceInCalendarDays, isSameDay, startOfDay } from 'date-fns';
import { Dispatch, SetStateAction } from 'react';
import { z } from 'zod';

import type { CollapseInfo } from './VivaRessourcenCalendars.utils.types';

import { clusterzuordnung, ZeitlicherAblauf, Zeitraum } from '../../../dtos';
import { addDaysToZeitraum, isDateInZeitraeume } from '../../../utils/zeitraum';
import {
  CalendarCollapsibleRow,
  CalendarEvent,
  CalendarExperteResource,
  CalendarGruppenraumResource,
  calendarResourceTypes,
  CalendarSeminarraumResource,
  CalendarStandortResource,
  isExperteEvent,
  isExperteResource,
  isSeminarraumResource,
} from '../ResourceCalendar';
import { isSeminarraumEvent } from '../ResourceCalendar/helperClasses';
import { AbstractCalendarResource } from '../ResourceCalendar/helperClasses/CalendarResource';
import {
  addValidationErrors,
  createEventsForClickedResource,
  isResourceSelectedAtClickedDate,
  removeConflictingEvents,
  removeEvent,
  setEventTimesFromAblauf,
} from '../ResourceCalendar/helperClasses/eventUtils';
import { View, views } from '../ResourceCalendar/ResourceCalendar';
import {
  findResourceOrThrow,
  inferAblaufFromEvents,
  inferAblaufFromStartDate,
  isEventUpdateAllowed,
} from './VivaRessourcenCalendars.utils';

export const onCalendarTimeRangeChanged = (
  newTimeRange: Zeitraum,
  setTimeRange: Dispatch<SetStateAction<Zeitraum>>,
): void => {
  setTimeRange(newTimeRange);
};

export const onViewChange = (
  _oldView: View,
  newView: View,
  setPendingEvents: Dispatch<SetStateAction<readonly CalendarEvent[]>>,
  setView: Dispatch<SetStateAction<View>>,
  lockEventTimes = false,
): void => {
  setPendingEvents((prev) =>
    prev.map((event) => ({
      // eslint-disable-next-line @typescript-eslint/no-misused-spread
      ...event,
      allDay: newView === views.WEEK,
      editable: true,
      resize: lockEventTimes ? false : event.type === 'experte' && newView === views.DAY,
      dragInTime: lockEventTimes ? false : newView === views.DAY ? event.type === 'experte' : true,
    })),
  );

  setView(newView);
};

export const handleExpansionChange = (
  collapsibleRow: CalendarCollapsibleRow,
  collapsed: boolean,
  setCollapseInfos: Dispatch<SetStateAction<CollapseInfo>>,
): void => {
  setCollapseInfos((prev): CollapseInfo => ({ ...prev, [collapsibleRow]: collapsed }));
};

type OnEventUpdatedProps = {
  updateEvent: MbscEventUpdatedEvent;
  pendingEvents: readonly CalendarEvent[];
  ablauf: Zeitraum[];
  setAblauf: Dispatch<SetStateAction<Zeitraum[]>>;
  zeitlicherAblauf: ZeitlicherAblauf | undefined;
  isPcCenterThema: boolean;
  istTerminreihe: boolean;
  fixedEvents: readonly CalendarEvent[];
  seminarraumResources: readonly CalendarSeminarraumResource[];
  experteResources: readonly CalendarExperteResource[];
  setPendingEvents: Dispatch<SetStateAction<readonly CalendarEvent[]>>;
};

export const onEventUpdated = ({
  updateEvent,
  pendingEvents,
  ablauf,
  setAblauf,
  zeitlicherAblauf,
  isPcCenterThema,
  istTerminreihe,
  fixedEvents,
  seminarraumResources,
  experteResources,
  setPendingEvents,
}: OnEventUpdatedProps): void => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- todo: replace CalendarEvent type with AbstractCalendarEvent
  const newEvent = updateEvent.event as CalendarEvent;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- todo: replace CalendarEvent type with AbstractCalendarEvent
  const oldEvent = updateEvent.oldEvent as CalendarEvent;
  const clickedResource = z.instanceof(AbstractCalendarResource).parse(updateEvent.resourceObj);

  newEvent.ressourceSapId = clickedResource.getRessourceSapId();

  if (isSeminarraumResource(clickedResource) && isSeminarraumEvent(newEvent)) {
    newEvent.raumKuerzel = clickedResource.raum.kuerzel;
    newEvent.locationKuerzel = clickedResource.location.kuerzel;
  }

  const dayDiff = differenceInCalendarDays(startOfDay(newEvent.start), startOfDay(oldEvent.start));

  let shiftedAblauf = [...ablauf];
  shiftedAblauf = shiftedAblauf.map((zeitraum) => {
    if ((istTerminreihe && isSameDay(oldEvent.start, zeitraum.start)) || !istTerminreihe) {
      return addDaysToZeitraum(zeitraum, dayDiff);
    }

    return zeitraum;
  });

  let events = pendingEvents.map((event) => {
    if (event.id === newEvent.id) {
      return newEvent;
    }

    const shouldShiftEvent = !istTerminreihe || (isExperteEvent(event) && isSameDay(event.start, oldEvent.start));

    if (!shouldShiftEvent) {
      return event;
    }

    const { start, end } = addDaysToZeitraum(event, dayDiff);
    event.start = start;
    event.end = end;

    return event;
  });
  // if shifted ablauf has less distinct days than the original ablauf, we reset it to the original
  const distinctDays = new Set(shiftedAblauf.map((zeitraum) => startOfDay(zeitraum.start).getTime()));
  if (distinctDays.size < ablauf.length) {
    shiftedAblauf = [...ablauf];
  }

  const newAblauf = inferAblaufFromEvents(events, zeitlicherAblauf ?? null, shiftedAblauf);
  setAblauf(newAblauf);

  // if an experte event was altered copy the new ablauf times to other events
  if (newEvent.type === calendarResourceTypes.EXPERTE) {
    setEventTimesFromAblauf(events, newAblauf);
  }

  if (oldEvent.resource !== newEvent.resource && isSeminarraumResource(clickedResource)) {
    events = events.filter(
      (event) =>
        event.type !== calendarResourceTypes.GRUPPENRAUM || event.locationKuerzel === clickedResource.location.kuerzel,
    );
  }

  addValidationErrors(events, fixedEvents, istTerminreihe, isPcCenterThema, seminarraumResources, experteResources);

  setPendingEvents(events);
};

// Enforce rules on drag or resize of events (called before the event is updated and rendered)
export const onEventUpdate = (
  updateEvent: MbscEventUpdateEvent,
  ablauf: Zeitraum[],
  istTerminreihe: boolean,
  view: View,
  pendingEvents: readonly CalendarEvent[],
  lockEventTimes = false,
): boolean => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- todo: replace CalendarEvent type with AbstractCalendarEvent
  const newEvent = updateEvent.event as CalendarEvent;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- todo: replace CalendarEvent type with AbstractCalendarEvent
  const oldEvent = updateEvent.oldEvent as CalendarEvent;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- todo: replace CalendarEvent type with AbstractCalendarEvent
  const newResource = updateEvent.resourceObj as AbstractCalendarResource;

  if (
    lockEventTimes &&
    (newEvent.start.getTime() !== oldEvent.start.getTime() ||
      newEvent.end.getTime() !== oldEvent.end.getTime() ||
      isExperteResource(newResource))
  ) {
    return false;
  }

  newEvent.type = newResource.type;

  const isUpdateAllowed = isEventUpdateAllowed(newEvent, oldEvent, ablauf);

  // disallow dragging experte events on existing ones in case of terminreihe, only for week view because in day view we drag in the same day just to other time
  if (
    istTerminreihe &&
    view !== 'day' &&
    pendingEvents.some(
      (prevEvent) =>
        (isExperteEvent(prevEvent) &&
          isExperteEvent(newEvent) &&
          prevEvent.ressourceSapId === newResource.getRessourceSapId() &&
          isSameDay(prevEvent.start, newEvent.start)) ||
        (prevEvent.id !== oldEvent.id &&
          isSameDay(prevEvent.start, oldEvent.start) &&
          prevEvent.ressourceSapId === newResource.getRessourceSapId()),
    )
  ) {
    return false;
  }

  return isUpdateAllowed;
};

type OnCalendarCellClickedProps = {
  args: MbscCellClickEvent;
  ablauf: Zeitraum[];
  pendingEvents: readonly CalendarEvent[];
  isPcCenterThema: boolean;
  zeitlicherAblauf: ZeitlicherAblauf | undefined;
  istTerminreihe: boolean;
  fixedEvents: readonly CalendarEvent[];
  seminarraumResources: CalendarSeminarraumResource[];
  gruppenraumResources: CalendarGruppenraumResource[];
  standortResources: CalendarStandortResource[];
  experteResources: CalendarExperteResource[];
  view: View;
  canEditAblauf?: boolean;
  canEditExperte?: boolean;
  setAblauf: Dispatch<SetStateAction<Zeitraum[]>>;
  setPendingEvents: Dispatch<SetStateAction<readonly CalendarEvent[]>>;
};

export const onCalendarCellClicked = ({
  args,
  ablauf,
  pendingEvents,
  isPcCenterThema,
  zeitlicherAblauf,
  istTerminreihe,
  fixedEvents,
  seminarraumResources,
  gruppenraumResources,
  standortResources,
  experteResources,
  view,
  canEditAblauf = true,
  canEditExperte = true,
  setAblauf,
  setPendingEvents,
}: OnCalendarCellClickedProps): void => {
  const rowHeaderCellResourceIds: (string | number)[] = [
    ...Object.values(calendarResourceTypes),
    ...Object.values(clusterzuordnung),
  ];

  const clickedResourceId = args.resource;

  // Ignore clicks on row header cells
  if (rowHeaderCellResourceIds.includes(clickedResourceId)) {
    return;
  }

  const clickedDate = args.date;
  const clickedResource = findResourceOrThrow(
    seminarraumResources,
    gruppenraumResources,
    standortResources,
    experteResources,
    String(clickedResourceId),
  );

  if (!canEditExperte && isExperteResource(clickedResource)) {
    return;
  }

  let newAblauf = [...ablauf];
  let events = [...pendingEvents];

  if (newAblauf.length === 0) {
    newAblauf = inferAblaufFromStartDate(clickedDate, zeitlicherAblauf ?? null);
  }

  // shift events and newAblauf to use the clicked date as the first day of the selection
  if (
    !isDateInZeitraeume(newAblauf, clickedDate) ||
    isResourceSelectedAtClickedDate(events, clickedResource.id, clickedDate)
  ) {
    if (!canEditAblauf) return;
    const dayDelta = differenceInCalendarDays(startOfDay(clickedDate), startOfDay(newAblauf[0].start));
    newAblauf = newAblauf.map((zeitraum) => addDaysToZeitraum(zeitraum, dayDelta));
    // eslint-disable-next-line @typescript-eslint/no-misused-spread
    events = events.map((event) => ({ ...event, ...addDaysToZeitraum(event, dayDelta) }));
  }

  // this check needs to be done after shifting the events, because the shifting changes event dates
  if (!isResourceSelectedAtClickedDate(events, clickedResource.id, clickedDate)) {
    const newEvents = createEventsForClickedResource(clickedDate, clickedResource, newAblauf, events, view);
    // some events can't coexists so we remove them (e.g. SeminarraumEvents and StandortEvents)
    events = removeConflictingEvents(clickedResource, events);
    events = [...events, ...newEvents];
  }

  // Reset experte blockungen geloescht flag on changes
  if (isExperteResource(clickedResource)) {
    // set geloescht false
    events.map((evt) => {
      if (isExperteEvent(evt)) evt.blockungData.geloescht = false;
    });
  }

  // Check selection validity e.g contains weekend or is blocked by other event
  addValidationErrors(events, fixedEvents, istTerminreihe, isPcCenterThema, seminarraumResources, experteResources);

  setPendingEvents(events);

  newAblauf = inferAblaufFromEvents(events, zeitlicherAblauf ?? null, newAblauf);
  setAblauf(newAblauf);
};

export const onCalendarEventClicked = (
  mbscEvent: MbscEventClickEvent,
  setPendingEvents: Dispatch<SetStateAction<readonly CalendarEvent[]>>,
  setAblauf: Dispatch<SetStateAction<Zeitraum[]>>,
  zeitlicherAblauf: ZeitlicherAblauf | undefined,
  oldAblauf?: Zeitraum[],
): void => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- todo: replace CalendarEvent type with AbstractCalendarEvent
  const event = mbscEvent.event as CalendarEvent;
  setPendingEvents((previousEvents) => {
    const newPreviousEvents = removeEvent(previousEvents, event.id);

    if (newPreviousEvents.length === 0) {
      setAblauf(oldAblauf ?? inferAblaufFromEvents([], zeitlicherAblauf ?? null));
    }

    return newPreviousEvents;
  });
};
